import React, { useState, useEffect } from 'react';
import {
  Typography,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Alert,
  Spin,
  Upload,
  message,
  Switch,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { uploadImage } from '../services/uploadService';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from '../services/categoriesService';

function CategoriesAdmin() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon_name: '',
    image_url: '',
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const data = await getCategories();
      setCategories(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const [form] = Form.useForm();
  const { Title } = Typography;

  const handleOpenModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      form.setFieldsValue({
        name: category.name,
        description: category.description || '',
        icon_name: category.icon_name || '',
        image_url: category.image_url || '',
        teaser_image_url: category.teaser_image_url || '',
        show_card_text: category.show_card_text || false,
        visible: category.visible || false,
      });
    } else {
      setEditingCategory(null);
      form.resetFields();
    }
    setOpenDialog(true);
  };

  const handleCloseModal = () => {
    setOpenDialog(false);
    setEditingCategory(null);
    form.resetFields();
  };

  const handleSubmit = async values => {
    setLoading(true);
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, values);
      } else {
        await createCategory(values);
      }
      fetchCategories();
      handleCloseModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async id => {
    Modal.confirm({
      title: 'Are you sure you want to delete this category?',
      onOk: async () => {
        setLoading(true);
        try {
          await deleteCategory(id);
          fetchCategories();
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },

    {
      title: 'Visible',
      dataIndex: 'visible',
      key: 'visible',
      render: visible => (visible ? 'Yes' : 'No'),
    },
    {
      title: 'Show Card Text',
      dataIndex: 'show_card_text',
      key: 'show_card_text',
      render: showCardText => (showCardText ? 'Yes' : 'No'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="text" icon={<EditOutlined />} onClick={() => handleOpenModal(record)} />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          />
        </Space>
      ),
    },
  ];

  if (loading)
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        <Spin size="large" />
      </div>
    );
  if (error) return <Alert message={error} type="error" />;

  return (
    <div style={{ padding: '24px' }}>
      <div
        style={{
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={2}>Categories</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenModal()}>
          Add Category
        </Button>
      </div>

      <Table columns={columns} dataSource={categories} rowKey="id" />

      <Modal
        title={editingCategory ? 'Edit Category' : 'Add Category'}
        open={openDialog}
        onCancel={handleCloseModal}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please input the category name!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="icon_name" label="Icon Name">
            <Input />
          </Form.Item>
          <Form.Item name="image_url" label="Main Image">
            <Upload
              maxCount={1}
              accept="image/*"
              beforeUpload={async file => {
                try {
                  const url = await uploadImage(file);
                  form.setFieldsValue({ image_url: url });
                  message.success('Image uploaded successfully');
                } catch (err) {
                  message.error('Failed to upload image');
                }
                return false;
              }}
            >
              <Button icon={<UploadOutlined />}>Upload Image</Button>
            </Upload>
            {form.getFieldValue('image_url') && (
              <img
                src={form.getFieldValue('image_url')}
                alt="Preview"
                style={{
                  marginTop: 8,
                  maxWidth: '100%',
                  maxHeight: 200,
                  objectFit: 'contain',
                }}
              />
            )}
          </Form.Item>
          <Form.Item name="teaser_image_url" label="Thumbnail(Card) Image">
            <Upload
              maxCount={1}
              accept="image/*"
              beforeUpload={async file => {
                try {
                  const url = await uploadImage(file);
                  form.setFieldsValue({ teaser_image_url: url });
                  message.success('Teaser image uploaded successfully');
                } catch (err) {
                  message.error('Failed to upload teaser image');
                }
                return false;
              }}
            >
              <Button icon={<UploadOutlined />}>Upload Teaser Image</Button>
            </Upload>
            {form.getFieldValue('teaser_image_url') && (
              <img
                src={form.getFieldValue('teaser_image_url')}
                alt="Teaser Preview"
                style={{
                  marginTop: 8,
                  maxWidth: '100%',
                  maxHeight: 200,
                  objectFit: 'contain',
                }}
              />
            )}
          </Form.Item>
          <Form.Item name="show_card_text" label="Show Card Text" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="visible" label="Visible" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleCloseModal}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingCategory ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default CategoriesAdmin;
